import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, Trash2, User, FileText, Upload, Save, ArrowRight, X } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import { Report, ReportType, ReportStatus, SalesDetails, SalesCustomer, SalesCustomerFile } from '../../types';
import useAppStore from '../../store/useAppStore';
import toast from 'react-hot-toast';
import { useForm, useFieldArray, SubmitHandler } from 'react-hook-form';

type SalesReportFormInputs = {
    totalCustomers: number;
    serviceType: string;
    customers: SalesCustomer[];
};

const ModernSalesReportsScreen: React.FC<{ reportToEdit: Report | null }> = ({ reportToEdit }) => {
    const { t, user } = useAppContext();
    const { addReport, updateReport } = useAppStore();
    const navigate = useNavigate();
    const [isSaving, setIsSaving] = useState(false);

    const { register, control, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<SalesReportFormInputs>({
        defaultValues: {
            customers: []
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "customers"
    });

    const isEditMode = !!reportToEdit;

    useEffect(() => {
        if (isEditMode && reportToEdit) {
            const details = reportToEdit.details as SalesDetails;
            reset(details);
        } else {
            reset({
                totalCustomers: 1,
                serviceType: 'تركيب نظام شمسي',
                customers: [{ id: Date.now(), name: '', phone: '', region: '', requestType: 'استفسار سعر', notes: '', files: [] }]
            });
        }
    }, [reportToEdit, isEditMode, reset]);

    const handleFileChange = (customerIndex: number, e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const currentFiles = watch(`customers.${customerIndex}.files`) || [];
            const fileList = Array.from(e.target.files);
            const newFiles: SalesCustomerFile[] = fileList.map((file: File) => ({
                id: `${file.name}-${Date.now()}`,
                file: file,
                fileName: file.name,
                url: ''
            }));
            setValue(`customers.${customerIndex}.files`, [...currentFiles, ...newFiles]);
        }
    };

    const removeFile = (customerIndex: number, fileId: string) => {
        const currentFiles = watch(`customers.${customerIndex}.files`) || [];
        setValue(`customers.${customerIndex}.files`, currentFiles.filter(f => f.id !== fileId));
    };

    const onSubmit: SubmitHandler<SalesReportFormInputs> = async (data) => {
        if (!user) return;
        setIsSaving(true);

        try {
            const reportDetails: SalesDetails = {
                ...data,
                totalCustomers: Number(data.totalCustomers) || data.customers.length,
            };

            if (isEditMode && reportToEdit) {
                const updatedReport: Report = {
                    ...reportToEdit,
                    details: reportDetails,
                    modifications: [
                        ...(reportToEdit.modifications || []),
                        { modifiedBy: user.name, timestamp: new Date().toISOString() }
                    ]
                };
                await updateReport(updatedReport);
                toast.success('تم تحديث التقرير بنجاح!');
            } else {
                const newReport: Omit<Report, 'id'> = {
                    employeeId: user.employeeId,
                    employeeName: user.name,
                    branch: user.branch,
                    department: user.department,
                    type: ReportType.Sales,
                    date: new Date().toISOString(),
                    status: ReportStatus.Pending,
                    details: reportDetails
                };
                await addReport(newReport);
                toast.success('تم حفظ تقرير المبيعات بنجاح!');
            }
            navigate('/log');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto space-y-8 animate-content-fade-in pb-20">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-blue-500/20 text-blue-400">
                            <FileText size={28} />
                        </div>
                        {isEditMode ? "تعديل تقرير مبيعات" : "تقرير موظف مبيعات"}
                    </h1>
                    <p className="text-slate-400">سجل نشاطاتك اليومية وتفاصيل العملاء</p>
                </div>
                <button
                    onClick={() => navigate(isEditMode ? '/log' : '/')}
                    className="p-3 rounded-full bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
                >
                    <X size={24} />
                </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                {/* Main Info Card */}
                <div className="glass-panel p-6 rounded-2xl space-y-6">
                    <h2 className="text-xl font-bold text-white border-b border-slate-700/50 pb-4">البيانات الأساسية</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-300">إجمالي العملاء</label>
                            <div className="relative">
                                <User className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                                <input
                                    type="number"
                                    {...register("totalCustomers", { required: true, valueAsNumber: true, min: 1 })}
                                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-3 pr-11 pl-4 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                                    placeholder="أدخل العدد الإجمالي للعملاء"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-300">{t('serviceType')}</label>
                            <div className="relative">
                                <select
                                    {...register("serviceType")}
                                    className="w-full appearance-none bg-slate-900/50 border border-slate-700 rounded-xl py-3 pr-4 pl-10 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                                >
                                    <option value="تركيب نظام شمسي">{t('salesServiceTypeSolarInstall')}</option>
                                    <option value="صيانة">{t('salesServiceTypeMaintenance')}</option>
                                    <option value="استشارة فنية">{t('salesServiceTypeTechnicalConsulting')}</option>
                                    <option value="معاينة موقع">{t('salesServiceTypeSiteSurvey')}</option>
                                    <option value="ألواح شمسية">{t('salesProductPanels')}</option>
                                    <option value="بطاريات">{t('salesProductBatteries')}</option>
                                    <option value="محولات">{t('salesProductInverters')}</option>
                                    <option value="ملحقات">{t('salesProductAccessories')}</option>
                                </select>
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Customers Section */}
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-white">بيانات العملاء (تفصيلي)</h2>
                        <button
                            type="button"
                            onClick={() => append({ id: Date.now(), name: '', phone: '', region: '', requestType: 'استفسار سعر', notes: '', files: [] })}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white transition-all text-sm font-medium border border-blue-500/30"
                        >
                            <PlusCircle size={18} />
                            <span>إضافة عميل جديد</span>
                        </button>
                    </div>

                    <div className="space-y-4">
                        {fields.map((field, index) => {
                            const customerFiles = watch(`customers.${index}.files`) || [];
                            return (
                                <div
                                    key={field.id}
                                    className="glass-card p-6 rounded-2xl relative animate-fade-in-up group border border-slate-700/50 hover:border-blue-500/30 transition-all"
                                    style={{ animationDelay: `${index * 100}ms` }}
                                >
                                    <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {fields.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => remove(index)}
                                                className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white transition-colors"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                                        <div className="space-y-1">
                                            <label className="text-xs text-slate-400">الاسم</label>
                                            <input
                                                {...register(`customers.${index}.name`, { required: true })}
                                                className="w-full bg-slate-900/30 border border-slate-700 rounded-lg py-2 px-3 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                                                placeholder="اسم العميل"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs text-slate-400">رقم الجوال</label>
                                            <input
                                                dir="ltr"
                                                {...register(`customers.${index}.phone`, { required: true })}
                                                className="w-full bg-slate-900/30 border border-slate-700 rounded-lg py-2 px-3 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all text-right"
                                                placeholder="05xxxxxxxx"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs text-slate-400">المنطقة/المدينة</label>
                                            <input
                                                {...register(`customers.${index}.region`, { required: true })}
                                                className="w-full bg-slate-900/30 border border-slate-700 rounded-lg py-2 px-3 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                                                placeholder="الرياض"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs text-slate-400">نوع الطلب</label>
                                            <select
                                                {...register(`customers.${index}.requestType`)}
                                                className="w-full bg-slate-900/30 border border-slate-700 rounded-lg py-2 px-3 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                                            >
                                                <option value="استفسار سعر">{t('customerRequestInquiryPrice')}</option>
                                                <option value="طلب عرض سعر">{t('customerRequestQuote')}</option>
                                                <option value="متابعة">{t('customerRequestFollowUp')}</option>
                                                <option value="شكوى">{t('customerRequestComplaint')}</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="space-y-1 mb-4">
                                        <label className="text-xs text-slate-400">الملاحظات</label>
                                        <textarea
                                            {...register(`customers.${index}.notes`)}
                                            className="w-full bg-slate-900/30 border border-slate-700 rounded-lg py-2 px-3 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all min-h-[80px]"
                                            placeholder="أضف أي ملاحظات إضافية هنا..."
                                        />
                                    </div>

                                    <div>
                                        <div className="flex items-center gap-3 mb-2">
                                            <label className="text-xs text-slate-400">المستندات</label>
                                            <button
                                                type="button"
                                                onClick={() => document.getElementById(`file-upload-${field.id}`)?.click()}
                                                className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
                                            >
                                                <Upload size={12} />
                                                <span>رفع ملفات</span>
                                            </button>
                                            <input
                                                type="file"
                                                multiple
                                                id={`file-upload-${field.id}`}
                                                className="hidden"
                                                onChange={(e) => handleFileChange(index, e)}
                                            />
                                        </div>

                                        {customerFiles.length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                {customerFiles.map(f => (
                                                    <div key={f.id} className="bg-slate-800 border border-slate-700 text-xs py-1 px-2 rounded-lg flex items-center gap-2 text-slate-300">
                                                        <span className="truncate max-w-[150px]">{f.fileName}</span>
                                                        <button type="button" onClick={() => removeFile(index, f.id)} className="text-slate-500 hover:text-red-400 transition-colors">
                                                            <X size={12} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-900/80 backdrop-blur-md border-t border-slate-800 z-50 flex justify-end gap-4 lg:pl-80 transition-all duration-300">
                    <button
                        type="button"
                        onClick={() => navigate(isEditMode ? '/log' : '/')}
                        className="px-6 py-3 rounded-xl text-slate-400 hover:text-white font-medium transition-colors"
                    >
                        إلغاء
                    </button>
                    <button
                        type="submit"
                        disabled={isSaving}
                        className="flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-blue-500/25 transition-all font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? (
                            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        ) : (
                            <>
                                <Save size={20} />
                                <span>{isEditMode ? "حفظ التعديلات" : "حفظ وإرسال التقرير"}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ModernSalesReportsScreen;
